import { Button, Card, MenuItem, Select, Stack, Typography, styled } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import { userService } from "../../../Core/Api/services";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { UpdateGroupsRequest } from "../../../Core/Api/Types/update-groups-request";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalGroupsChangeRoleElementProps = {
  groups: number[];
  onClose?: (props: CloseClbProps) => void;
};
export type ModalGroupsChangeRoleProps = CommonModalProps<ModalGroupsChangeRoleElementProps>;

const ModalGroupsChangeRole = React.forwardRef<any, ModalGroupsChangeRoleProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, groups, ...modalProps } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();

  const [roleSelected, setRoleSelected] = React.useState<DbUserGroupRoleInfos | null>();
  const [roles, setRoles] = React.useState<DbUserGroupRoleInfos[]>([]);

  React.useEffect(() => {
    userService.GetGroupRolesList().then((response) => {
      if (response.groupRoles) setRoles(response.groupRoles);
    });
  }, []);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    if (groups.length > 0) {
      const request: UpdateGroupsRequest = {
        groupIds: groups,
        roleId: roleSelected?.id || null,
      };

      userService.UpdateGroups(request).then(function (response) {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.groupUpdatedWithNewRole"), "success");
        }
      });
      onClose && onClose({ confirm: true });
      closeModal();
    }
  };

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.changeNewRole")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      height={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="primary" variant="contained" onClick={handleConfirmClick}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            value={roleSelected?.id || undefined}
            sx={{ width: "100%", mb: 2 }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            onChange={(e) => {
              setRoleSelected(roles?.find((role) => role.id === (e.target.value as number)) || null);
            }}
          >
            <MenuItem value={undefined}>{t("menu:item.noRole")}</MenuItem>
            {roles.map((role) => (
              <MenuItem value={role.id} key={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {roleSelected && (
          <CardRoles key={roleSelected.id}>
            <Stack alignItems="flex-start">
              <UserGroupChip roleId={roleSelected.id} roles={roles} />
              <Typography variant="body2" color="text.tier" mt={1}>
                {roleSelected.description}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" ml={1.5}></Stack>
          </CardRoles>
        )}
      </Stack>
    </Modal>
  );
});

export default ModalGroupsChangeRole;

const CardRoles = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  border: `1px solid transparent`,
  transition: "border-color .3s, background .3s",

  "&:hover": {
    borderColor: theme.palette.backgroundAlpha.border,
    backgroundColor: theme.palette.backgroundAlpha.hover,
  },
}));
